import type { Color, PerspectiveCamera, Scene, WebGLRenderer, WebGLRenderTarget, Object3D } from 'three'
import { DoubleSide, MeshBasicMaterial, NotEqualStencilFunc, ReplaceStencilOp, Mesh } from 'three'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { LAYERS } from '@/lib/constants/model'
import type { Model } from './model'

export enum HIGHLIGHT_COLORS {
  BLUE = 0x0080ff,
  GREEN = 0x6ee7b7,
}

class LayerHighlightManager extends RenderPass {
  public currentModel: Model | null = null
  private _material: MeshBasicMaterial
  private _layerMask: number
  private _highlightedLayerId: number | null = null

  // RenderPass clears target by default
  override clear = false

  constructor(
    params: { color: Color | number | string; opacity: number; layerMask: number },
    scene: Scene,
    currentCamera: PerspectiveCamera
  ) {
    const material = new MeshBasicMaterial({
      color: params.color,
      opacity: params.opacity,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: DoubleSide,
      stencilWrite: true,
      stencilFunc: NotEqualStencilFunc,
      stencilRef: 1,
      stencilZPass: ReplaceStencilOp,
    })

    super(scene, currentCamera, material)

    this._material = material
    this._layerMask = params.layerMask | 0
  }

  setColor(color: Color | number | string): void {
    this._material.color.set(color)
  }

  setLayerHighlight = ({ layerId, shouldHighlight }: { layerId: number; shouldHighlight: boolean }) => {
    if (!this.currentModel) return

    const sceneNode = this.currentModel.layerNodesById.get(layerId)

    const processChild = (child: Object3D) => {
      if (child.userData.labelId) return

      if (shouldHighlight) {
        child.layers.enable(LAYERS.HIGHLIGHT)
      } else {
        if (child instanceof Mesh) child.geometry.dispose()
        this.dispose()
        child.layers.disable(LAYERS.HIGHLIGHT)
      }
    }

    if (sceneNode) {
      if (!shouldHighlight && layerId === this._highlightedLayerId) {
        sceneNode.visible = sceneNode.userData.wasVisible
        this._highlightedLayerId = null
        delete sceneNode.userData.isVisibleForLayerHighlighting
        delete sceneNode.userData.wasVisible
      } else if (shouldHighlight) {
        if (this._highlightedLayerId !== layerId) {
          this._highlightedLayerId = layerId
          sceneNode.userData.isVisibleForLayerHighlighting = true
          sceneNode.userData.wasVisible = sceneNode.visible
        }

        sceneNode.visible = true
      }

      sceneNode.traverse(processChild)
    }
  }

  removeCurrentLayerHighlight() {
    if (this._highlightedLayerId) {
      this.setLayerHighlight({ layerId: this._highlightedLayerId, shouldHighlight: false })
      this._highlightedLayerId = null
      this.dispose()
    }
  }

  override render(
    renderer: WebGLRenderer,
    writeBuffer: WebGLRenderTarget,
    readBuffer: WebGLRenderTarget,
    deltaTime: number,
    maskActive: boolean
  ): void {
    // Set the layer mask
    const oldMask = this.camera.layers.mask
    this.camera.layers.set(this._layerMask)

    // Render the scene
    super.render(renderer, writeBuffer, readBuffer, deltaTime, maskActive)

    // Restore the layer mask
    this.camera.layers.mask = oldMask
  }

  override dispose() {
    this._material.dispose()
  }
}

export { LayerHighlightManager }
